<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else-if="featureUnavailable" class="m-3">
    <feature-not-available back-route="reports" back-text="_plan.Back to reports" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
      <div class="d-flex align-items-center gap-2">
        <div>
          <h5 class="fw-bold mb-0">
            <i class="fa-solid fa-file-invoice-dollar me-2" style="color: #64748b;"></i>{{ $t('VAT Sales List') }}
          </h5>
          <small class="text-muted">{{ $t('VAT sales report for PP.30 filing') }}</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-success btn-sm" @click="exportExcel" :disabled="loading || !hasRows">
          <i class="fa-solid fa-file-excel me-1"></i>{{ $t('Export Excel') }}
        </button>
        <!-- ไฟล์เดียวจบสำหรับส่งบัญชี: สรุป + ภาษีขาย + ภาษีซื้อ ของช่วงที่เลือก -->
        <button
          class="btn btn-success btn-sm"
          @click="exportTaxPackage"
          :disabled="loading || packing || !hasRows"
        >
          <span v-if="packing"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-zipper me-1"></i>{{ $t('Send to Bookkeeper') }}</span>
        </button>
        <button class="btn btn-outline-primary btn-sm" @click="downloadPdf" :disabled="pdfLoading || loading">
          <i class="fa-solid fa-download me-1"></i>{{ $t('Download') }}
        </button>
        <button class="btn btn-primary btn-sm" @click="previewPdf" :disabled="pdfLoading || loading">
          <span v-if="pdfLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-pdf me-1"></i>{{ $t('Preview PDF') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body py-2 px-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select v-model="filters.branch" class="form-select form-select-sm" style="min-width: 140px; max-width: 200px;" :disabled="loading">
            <option value="all">{{ $t('All Branches') }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <div class="input-group input-group-sm w-auto date-range-input-group">
            <span class="input-group-text"><i class="fa-regular fa-calendar"></i></span>
            <date-range-picker
              v-model="filters.date_range"
              :date-range="filters.date_range"
              :locale-data="dateRangeLocale"
              :ranges="dateRanges"
              :opens="'right'"
              :auto-apply="true"
              :show-dropdowns="true"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate && picker.endDate">
                  {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
                </span>
                <span v-else class="text-muted">{{ $t('Select date range') }}</span>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReport">{{ $t('Retry') }}</button>
    </div>

    <div :class="{ 'report-loading': loading }">

    <!-- KPI Cards -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-4 col-xl">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Total VAT') }}</div>
            <div class="fw-bold fs-5 text-primary">{{ formatCurrency(summary.total_vat) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4 col-xl">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Total Taxable') }}</div>
            <div class="fw-bold fs-5 text-success">{{ formatCurrency(summary.total_taxable) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4 col-xl">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Service Charge') }}</div>
            <div class="fw-bold fs-5">{{ formatCurrency(summary.service_charge) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4 col-xl">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Total Amount') }}</div>
            <div class="fw-bold fs-5">{{ formatCurrency(summary.total_amount) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4 col-xl">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Full Tax Invoices') }}</div>
            <div class="fw-bold fs-4">{{ summary.total_invoices || 0 }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4 col-xl">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Voided') }}</div>
            <div class="fw-bold fs-4 text-danger">{{ summary.voided_count || 0 }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabs — nav-custom คือสไตล์แท็บของระบบ (ขีดล่างสีธีม) เหมือนหน้าจัดการบิล/สาขา -->
    <div class="d-flex border-bottom mb-3 justify-content-between align-items-end">
      <div class="overflow-auto hide-scrollbar flex-grow-1">
        <ul class="nav nav-custom flex-nowrap">
          <li
            v-for="tab in tabs"
            :key="tab.key"
            class="nav-item cursor-pointer"
            @click="activeTab = tab.key"
          >
            <a class="nav-link text-nowrap" :class="{ active: activeTab === tab.key }">
              {{ $t(tab.label) }}
              <span
                class="badge rounded-pill ms-1"
                :class="activeTab === tab.key ? 'bg-primary' : 'bg-secondary-subtle text-secondary-emphasis'"
              >
                {{ tabCounts[tab.key] || 0 }}
              </span>
            </a>
          </li>
        </ul>
      </div>
    </div>

    <!-- Daily summary (ยกมาจากรายงานภาษีเดิมที่ถูกยุบเข้ามา) -->
    <div v-if="activeTab === 'daily'" class="card border-0 shadow-sm mb-4">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">{{ $t('Date') }}</th>
                <th class="text-end">{{ $t('Gross Sales') }}</th>
                <th class="text-end">{{ $t('Net Sales') }}</th>
                <th class="text-end">{{ $t('VAT') }}</th>
                <th class="text-end pe-3">{{ $t('Service Charge') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!daily.length && !loading">
                <td colspan="5" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr v-for="row in daily" :key="row.date">
                <td class="ps-3">{{ row.date }}</td>
                <td class="text-end">{{ formatCurrency(row.gross) }}</td>
                <td class="text-end">{{ formatCurrency(row.net) }}</td>
                <td class="text-end fw-semibold text-primary">{{ formatCurrency(row.vat) }}</td>
                <td class="text-end pe-3">{{ formatCurrency(row.service_charge) }}</td>
              </tr>
            </tbody>
            <tfoot v-if="daily.length" class="table-light fw-bold">
              <tr>
                <td class="ps-3">{{ $t('Total') }}</td>
                <td class="text-end">{{ formatCurrency(summary.gross_sales) }}</td>
                <td class="text-end">{{ formatCurrency(summary.total_taxable) }}</td>
                <td class="text-end text-primary">{{ formatCurrency(summary.total_vat) }}</td>
                <td class="text-end pe-3">{{ formatCurrency(summary.service_charge) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!--
      ยอดขายรายวันแยกตามเครื่อง POS (api/docs/vat-compliance.md D10)
      ⚠️ เลขที่แสดงคือ "เลขแรก/เลขสุดท้าย" ของเครื่องนั้น ไม่ใช่ช่วงที่ต่อเนื่อง
      เพราะ counter เดินต่อสาขา หลายเครื่องในสาขาเดียวกันจึงสลับเลขกัน (D9)
    -->
    <div v-else-if="activeTab === 'devices'" class="card border-0 shadow-sm mb-4">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">{{ $t('Date') }}</th>
                <th>{{ $t('POS Device') }}</th>
                <th>{{ $t('POS ID') }}</th>
                <th class="text-end">{{ $t('Documents') }}</th>
                <!--
                  แสดงเลขที่ออกจริงแบบย่อเป็นช่วง แทน "เลขแรก/เลขสุดท้าย" ที่ชวนให้เอาไปลบกัน
                  แล้วสรุปผิดว่าจำนวนใบไม่ตรง (เลขรันต่อสาขา เครื่องหลายเครื่องจึงสลับเลขกัน)
                -->
                <th>{{ $t('Invoice Numbers Issued') }}</th>
                <th class="text-end">{{ $t('Taxable') }}</th>
                <th class="text-end">{{ $t('VAT') }}</th>
                <th class="text-end">{{ $t('Total Amount') }}</th>
                <!--
                  ยอดขายสะสม (Grand Total) — สรรพากรใช้พิสูจน์ว่าไม่มีการลบบิลย้อนหลัง
                  ต้องรันต่อเนื่องต่อเครื่อง ห้ามรีเซ็ตเมื่อปิดวัน (vat-compliance.md D10)
                -->
                <th class="text-end">{{ $t('Previous Grand Total') }}</th>
                <th class="text-end">{{ $t('Current Grand Total') }}</th>
                <!-- สลิปปิดยอดวันที่พิมพ์จากเครื่องความร้อน — ตัวที่สรรพากรขอดูตอนตรวจเครื่อง -->
                <th class="text-end pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!devices.length && !loading">
                <td colspan="11" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr v-for="(row, i) in devices" :key="i">
                <td class="ps-3 text-nowrap">{{ row.date }}</td>
                <td>
                  <div>{{ row.device_name || $t('Unassigned device') }}</div>
                  <div v-if="row.branch" class="text-muted small">{{ row.branch }}</div>
                </td>
                <td>{{ row.pos_device_code || '-' }}</td>
                <td class="text-end">{{ row.bill_count }}</td>
                <td class="small">
                  <template v-if="row.invoice_no_list">
                    <div class="text-muted">{{ row.invoice_no_prefix }}</div>
                    <div>{{ row.invoice_no_list }}</div>
                  </template>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="text-end">{{ formatCurrency(row.taxable_amount) }}</td>
                <td class="text-end fw-semibold text-primary">{{ formatCurrency(row.vat_amount) }}</td>
                <td class="text-end fw-semibold">{{ formatCurrency(row.total_amount) }}</td>
                <!-- แถว "ไม่ระบุเครื่อง" ไม่มียอดสะสม เพราะไม่ได้ออกจากเครื่องที่ขออนุมัติไว้ -->
                <td class="text-end text-muted small">
                  {{ row.grand_total_before === null ? '-' : formatCurrency(row.grand_total_before) }}
                </td>
                <td class="text-end fw-semibold">
                  {{ row.grand_total_after === null ? '-' : formatCurrency(row.grand_total_after) }}
                </td>
                <td class="text-end pe-3">
                  <button
                    type="button"
                    class="btn btn-sm btn-light border"
                    :title="$t('Print Z-Report slip')"
                    :disabled="slipRow !== null"
                    @click="printSlip(row, i)"
                  >
                    <i class="fa-solid" :class="slipRow === i ? 'fa-spinner fa-spin' : 'fa-receipt'" />
                  </button>
                </td>
              </tr>
            </tbody>
            <tfoot v-if="devices.length" class="table-light fw-bold">
              <tr>
                <td class="ps-3" colspan="5">{{ $t('Total') }}</td>
                <td class="text-end">{{ formatCurrency(summary.total_taxable) }}</td>
                <td class="text-end text-primary">{{ formatCurrency(summary.total_vat) }}</td>
                <td class="text-end">{{ formatCurrency(summary.total_amount) }}</td>
                <td colspan="3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div class="border-top px-3 py-2 text-muted" style="font-size: 12px;">
          <div>
            * {{ $t('First/Last No. are not a continuous range — the counter runs per branch so devices in the same branch interleave numbers.') }}
          </div>
          <div>
            * {{ $t('Grand Total runs continuously per device from its first bill and is never reset.') }}
          </div>
        </div>
      </div>
    </div>

    <!-- Document rows -->
    <div v-else class="card border-0 shadow-sm mb-4">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3 text-end" style="width: 50px;">#</th>
                <th>{{ $t('Date') }}</th>
                <th>{{ $t('Document') }}</th>
                <th>{{ $t('Invoice No.') }} / {{ $t('Bill No.') }}</th>
                <th>{{ $t('Customer') }}</th>
                <th class="text-end">{{ $t('Taxable') }}</th>
                <th class="text-end">{{ $t('VAT') }}</th>
                <th class="text-end">{{ $t('Total Amount') }}</th>
                <th class="text-center pe-3">{{ $t('Status') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!visibleRows.length && !loading">
                <td colspan="9" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr
                v-for="row in visibleRows"
                :key="row.seq"
                :class="{
                  'table-danger-subtle text-decoration-line-through text-muted': row.is_voided,
                  'table-abbr-daily': row.type === 'abbr_daily',
                }"
              >
                <td class="ps-3 text-end">{{ row.seq }}</td>
                <td class="text-nowrap">{{ row.date }}</td>
                <td>
                  <div class="fw-semibold">{{ documentLabel(row) }}</div>
                  <div v-if="row.branch" class="text-muted small">{{ row.branch }}</div>
                  <!--
                    ช่องทางขาย — บัญชีต้องแยกได้ว่ายอดไหนเก็บกับแพลตฟอร์ม ไม่ใช่เงินในลิ้นชัก
                    (api/docs/vat-compliance.md D10) ถังสรุปรายวันแยกตามช่องทางมาแล้วจาก API
                  -->
                  <div v-if="row.channel" class="small">
                    <span class="badge bg-info-subtle text-info-emphasis">{{ row.channel }}</span>
                  </div>
                  <!-- บรรทัดหักยอดคืน: โชว์เลขใบกำกับเดิมที่ถูกลด + เหตุผล ให้บัญชีเอาไปออกใบลดหนี้ต่อ -->
                  <div v-if="row.type === 'refund' && row.invoice_no" class="text-muted small">
                    {{ $t('Ref') }}: {{ row.invoice_no }}
                  </div>
                  <div v-if="row.type === 'refund' && row.void_reason" class="text-muted small">
                    {{ row.void_reason }}
                  </div>
                </td>
                <td>{{ row.type === 'abbr_daily' ? billRangeLabel(row) : row.bill_no }}</td>
                <td>
                  <div>{{ row.type === 'abbr_daily' ? $t('Walk-in customers') : (row.customer_name || '-') }}</div>
                  <div v-if="row.customer_tax_id" class="text-muted small">
                    {{ row.customer_tax_id }}
                    <span v-if="row.customer_branch">({{ row.customer_branch }})</span>
                  </div>
                </td>
                <td class="text-end">{{ formatCurrency(row.taxable_amount) }}</td>
                <td class="text-end">{{ formatCurrency(row.vat_amount) }}</td>
                <td class="text-end fw-semibold">{{ formatCurrency(row.total_amount) }}</td>
                <td class="text-center pe-3">
                  <span
                    v-if="row.is_voided"
                    class="badge bg-danger text-decoration-none d-inline-block"
                    :title="voidTooltip(row)"
                  >
                    {{ $t('Voided') }}
                  </span>
                  <span v-else class="badge" :class="typeBadgeClass(row)">{{ $t(typeLabel(row)) }}</span>
                </td>
              </tr>
            </tbody>
            <tfoot v-if="visibleRows.length" class="table-light fw-bold">
              <tr>
                <td class="ps-3" colspan="5">
                  {{ $t('Total') }}
                  <span class="text-muted fw-normal small">
                    ({{ activeTab === 'full' ? $t('full tax invoices only') : $t('active only') }})
                  </span>
                </td>
                <td class="text-end text-success">{{ formatCurrency(visibleTotals.taxable) }}</td>
                <td class="text-end text-primary">{{ formatCurrency(visibleTotals.vat) }}</td>
                <td class="text-end">{{ formatCurrency(visibleTotals.total) }}</td>
                <td class="pe-3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <!--
          ใบกำกับเต็มรูปแสดงยอด ฿0 โดยตั้งใจ (D12) — ใบย่อคือเอกสารแม่ที่ยอดขายเกาะอยู่
          ใส่ยอดที่ใบเต็มรูปด้วยจะนับยอดขายก้อนเดียวกัน 2 ครั้ง
          แต่หน้าจอที่โชว์ ฿0 เฉย ๆ ดูเหมือนระบบพัง ต้องอธิบายไว้ใต้ตาราง
        -->
        <!--
          ต้องขึ้นทั้งแท็บ "ใบกำกับเต็มรูป" และ "เอกสารทั้งหมด"
          แท็บเอกสารทั้งหมดสับสนกว่าด้วยซ้ำ เพราะบรรทัด ฿0 ไปนั่งปนกับบรรทัดที่มียอดจริง
          คนอ่านจะนึกว่าบางใบคำนวณไม่ขึ้น
        -->
        <div v-if="['full', 'all'].includes(activeTab)" class="border-top px-3 py-2 text-muted" style="font-size: 12px;">
          * {{ $t('Full tax invoices show zero here — the amount sits on the abbreviated invoice it replaces so the sale is not counted twice.') }}
        </div>
      </div>
    </div>

    </div><!-- /loading wrapper -->

    <div v-if="loading" class="report-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { Reports } from '@/services/endpoint/reports.service';
import { exportRowsToXlsx, exportSheetsToXlsx, MONEY_FMT } from '@/utils/excelExport';
import { printZReportSlip } from '@/utils/zReportSlip';
import { printerPaperWidth } from '@/utils/printerSettings';
import reportMixin from './reportMixin';

/**
 * รายงานภาษีขาย — ครอบคลุมยอดขายทั้งหมด ไม่ใช่เฉพาะบิลที่ลูกค้าขอใบกำกับ
 * (silver-spoon-api/docs/vat-compliance.md — Phase 1 + D5)
 *
 * 3 แท็บ: สรุปรายวัน (ยุบรายงานภาษีเข้ามา) / ใบกำกับเต็มรูป / เอกสารทั้งหมด
 * ทุกแท็บอ่านจาก payload ก้อนเดียว ตัวเลขจึงขัดกันเองไม่ได้
 */
export default {
  name: 'ReportVatSalesList',
  mixins: [reportMixin],
  data() {
    // Default to the current calendar month — the typical PP.30 filing period.
    const start = moment().startOf('month').toDate();
    const end = moment().endOf('month').toDate();
    return {
      reportLabel: 'VAT Sales List',
      filters: {
        branch: 'all',
        date_range: { startDate: start, endDate: end },
        dateFrom: moment(start).format('YYYY-MM-DD'),
        dateTo: moment(end).format('YYYY-MM-DD'),
      },
      activeTab: 'daily',
      packing: false,
      tabs: [
        { key: 'daily', label: 'Daily Summary' },
        // Daily Tax Report ต่อเครื่อง — สรรพากรใช้ตรวจคู่กับเครื่องที่อนุมัติไว้ (ภ.พ.06)
        { key: 'devices', label: 'By POS Device' },
        { key: 'full', label: 'Full Tax Invoices' },
        { key: 'all', label: 'All Documents' },
      ],
      summary: {
        total_invoices: 0,
        voided_count: 0,
        total_bills: 0,
        gross_sales: 0,
        total_taxable: 0,
        total_vat: 0,
        total_amount: 0,
        service_charge: 0,
      },
      daily: [],
      // ยอดรายวันแยกตามเครื่อง POS (Daily Tax Report — api/docs/vat-compliance.md D10)
      devices: [],
      // แถวที่กำลังพิมพ์สลิป Z-Report (index) — กันกดรัวจนสั่งพิมพ์ซ้อน
      slipRow: null,
      // ชื่อผู้ประกอบการ/เลขผู้เสียภาษี สำหรับหัวสลิป (filter.shop_tax)
      shopTax: {},
      invoices: [],
    };
  },
  computed: {
    // ตัวเลขบนแท็บ — บอกล่วงหน้าว่าแต่ละมุมมองมีกี่แถว ไม่ต้องกดเข้าไปดู
    tabCounts() {
      return {
        daily: this.daily.length,
        devices: this.devices.length,
        full: this.invoices.filter(r => r.type === 'full').length,
        all: this.invoices.length,
      };
    },
    visibleRows() {
      if (this.activeTab === 'full') {
        return this.invoices.filter(r => r.type === 'full');
      }
      return this.invoices;
    },
    // ยอดท้ายตารางต้องเป็นยอดของ "สิ่งที่เห็นอยู่" ไม่ใช่ยอดรวมทั้งรายงาน
    // (แท็บใบกำกับเต็มรูปเป็นแค่ส่วนย่อยของยอดขายทั้งหมด)
    visibleTotals() {
      return this.visibleRows.reduce((acc, r) => ({
        taxable: acc.taxable + this.toNum(r.taxable_amount),
        vat: acc.vat + this.toNum(r.vat_amount),
        total: acc.total + this.toNum(r.total_amount),
      }), { taxable: 0, vat: 0, total: 0 });
    },
    hasRows() {
      if (this.activeTab === 'daily') return this.daily.length > 0;
      if (this.activeTab === 'devices') return this.devices.length > 0;
      return this.visibleRows.length > 0;
    },
  },
  methods: {
    async fetchReport() {
      const res = await Reports.vatSalesList(this.buildFetchParams());
      const payload = res?.data?.data || {};
      const filter = res?.data?.filter || {};
      this.branches = filter.branches || [];
      this.summary = payload.summary || this.summary;
      this.daily = payload.daily || [];
      this.devices = payload.devices || [];
      this.shopTax = filter.shop_tax || {};
      this.invoices = payload.invoices || [];
    },

    typeLabel(row) {
      if (row.type === 'full') return 'Full';
      if (row.type === 'abbr_daily') return 'Abbreviated (daily)';
      // ยอดคืนของบิลงวดก่อน — ลงในงวดที่คืนเงินจริง ไม่ย้อนไปแก้งวดที่ยื่นภาษีไปแล้ว
      if (row.type === 'refund') return 'Refund';
      return 'Abbreviated';
    },

    typeBadgeClass(row) {
      if (row.type === 'full') return 'bg-success';
      if (row.type === 'abbr_daily') return 'bg-secondary';
      if (row.type === 'refund') return 'bg-warning text-dark';
      return 'bg-info';
    },

    documentLabel(row) {
      if (row.type === 'abbr_daily') return this.$t('Abbreviated tax invoices (receipts)');
      if (row.type === 'refund') return this.$t('Refund of an earlier period');
      return row.invoice_no;
    },

    /**
      * ช่วง "เลขใบกำกับภาษี" ของบรรทัดสรุปรายวัน — ไม่ใช่เลขบิล
      * เพราะบรรทัดนี้คือยอดใบกำกับอย่างย่อที่ยื่น สรรพากรต้องกระทบยอดกับทะเบียนใบกำกับได้ (D9)
      * "INV-M-001-202609-000012 – INV-M-001-202609-000087 (76)"
      *
      * บิลเก่าที่ปิดก่อนมี D9 ไม่มีเลขนี้ → โชว์แค่จำนวนใบ ยอดยังนับครบตามปกติ
      */
    billRangeLabel(row) {
      const from = row.invoice_no_from || '';
      const to = row.invoice_no_to || '';
      const range = from && to && from !== to ? `${from} – ${to}` : (from || to);
      const count = row.bill_count || 0;
      return range ? `${range} (${count})` : (count ? `(${count})` : '');
    },

    voidTooltip(row) {
      const parts = [];
      if (row.voided_at) parts.push(this.$t('Voided At') + ': ' + row.voided_at);
      if (row.voided_by) parts.push(this.$t('Voided By') + ': ' + row.voided_by);
      if (row.void_reason) parts.push(this.$t('Reason') + ': ' + row.void_reason);
      return parts.join('\n');
    },

    toNum(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    },

    /**
     * พิมพ์สลิป Z-Report ของแถวนั้น (1 เครื่อง / 1 วัน)
     *
     * ตัวเลขส่งจาก row ตรง ๆ ไม่คำนวณใหม่ → สลิปกับตารางบนจอเป็นชุดเดียวกันเสมอ
     * (silver-spoon-api/docs/vat-compliance.md D13)
     */
    async printSlip(row, index) {
      if (this.slipRow !== null) return;
      this.slipRow = index;
      try {
        await printZReportSlip({
          row,
          shopTax: this.shopTax,
          // raster ตามกระดาษของเครื่องพิมพ์ที่ต่อกับ "เครื่องนี้" (ไม่ใช่ของสาขาในแถว)
          // ไม่งั้นสั่ง 576 dots ลงเครื่อง 58mm แล้วขอบขวาโดนตัด (ต่างจากทาง PDF ที่ไดรเวอร์ย่อให้)
          paperWidth: printerPaperWidth(),
          fmt: (v) => this.formatCurrencyRaw(v),
          t: (str) => this.$t(str),
          brandName: this.$ln(this.$brand),
        });
      } catch (e) {
        console.error('Z-Report slip print error:', e);
        this.$toast.error(this.$t('Print failed') + ': ' + (e.message || e));
      } finally {
        this.slipRow = null;
      }
    },
    exportExcel() {
      const from = this.formatDate(this.filters.date_range.startDate);
      const to = this.formatDate(this.filters.date_range.endDate);
      const header = [
        [this.$t('VAT Sales List')],
        [this.selectedBranchLabel + '  |  ' + from + ' — ' + to],
        [
          this.$t('Total Taxable') + ': ' + this.toNum(this.summary.total_taxable),
          this.$t('Total VAT') + ': ' + this.toNum(this.summary.total_vat),
          this.$t('Service Charge') + ': ' + this.toNum(this.summary.service_charge),
          this.$t('Total Amount') + ': ' + this.toNum(this.summary.total_amount),
        ],
        [],
      ];

      if (this.activeTab === 'devices') {
        exportRowsToXlsx({
          filename: `vat-sales-by-device-${from}-${to}.xlsx`,
          sheetName: 'By POS Device',
          columns: [
            { header: this.$t('Date'), key: 'date', width: 12 },
            { header: this.$t('Branch'), key: 'branch', width: 18 },
            { header: this.$t('POS Device'), value: r => r.device_name || this.$t('Unassigned device'), width: 20 },
            { header: this.$t('POS ID'), key: 'pos_device_code', width: 10 },
            { header: this.$t('Documents'), value: r => this.toNum(r.bill_count), width: 10 },
            // ไม่ใช่ช่วงต่อเนื่อง — counter เดินต่อสาขา เครื่องในสาขาเดียวกันสลับเลขกันได้
            {
              header: this.$t('Invoice Numbers Issued'),
              value: r => (r.invoice_no_list ? `${r.invoice_no_prefix}${r.invoice_no_list}` : ''),
              width: 44,
            },
            { header: this.$t('Taxable'), value: r => this.toNum(r.taxable_amount), width: 14, numFmt: MONEY_FMT },
            { header: this.$t('VAT'), value: r => this.toNum(r.vat_amount), width: 14, numFmt: MONEY_FMT },
            { header: this.$t('Total Amount'), value: r => this.toNum(r.total_amount), width: 16, numFmt: MONEY_FMT },
            // ยอดขายสะสมต่อเครื่อง — แถว "ไม่ระบุเครื่อง" ไม่มี (ไม่ได้ออกจากเครื่องที่ขออนุมัติ)
            {
              header: this.$t('Previous Grand Total'),
              value: r => (r.grand_total_before === null ? '' : this.toNum(r.grand_total_before)),
              width: 18,
              numFmt: MONEY_FMT,
            },
            {
              header: this.$t('Current Grand Total'),
              value: r => (r.grand_total_after === null ? '' : this.toNum(r.grand_total_after)),
              width: 18,
              numFmt: MONEY_FMT,
            },
          ],
          rows: this.devices,
          preRows: header,
          postRows: [
            [
              this.$t('Total'), '', '', '', '', '',
              this.toNum(this.summary.total_taxable),
              this.toNum(this.summary.total_vat),
              this.toNum(this.summary.total_amount),
            ],
            [],
            ['* ' + this.$t('First/Last No. are not a continuous range — the counter runs per branch so devices in the same branch interleave numbers.')],
            ['* ' + this.$t('Grand Total runs continuously per device from its first bill and is never reset.')],
          ],
        });
        return;
      }

      if (this.activeTab === 'daily') {
        exportRowsToXlsx({
          filename: `vat-sales-daily-${from}-${to}.xlsx`,
          sheetName: 'VAT Daily',
          columns: [
            { header: this.$t('Date'), key: 'date', width: 12 },
            { header: this.$t('Gross Sales'), value: r => this.toNum(r.gross), width: 14, numFmt: MONEY_FMT },
            { header: this.$t('Net Sales'), value: r => this.toNum(r.net), width: 14, numFmt: MONEY_FMT },
            { header: this.$t('VAT'), value: r => this.toNum(r.vat), width: 14, numFmt: MONEY_FMT },
            { header: this.$t('Service Charge'), value: r => this.toNum(r.service_charge), width: 14, numFmt: MONEY_FMT },
          ],
          rows: this.daily,
          preRows: header,
          postRows: [[
            this.$t('Total'),
            this.toNum(this.summary.gross_sales),
            this.toNum(this.summary.total_taxable),
            this.toNum(this.summary.total_vat),
            this.toNum(this.summary.service_charge),
          ]],
        });
        return;
      }

      const columns = [
        { header: '#', key: 'seq', width: 6 },
        { header: this.$t('Date'), key: 'date', width: 12 },
        { header: this.$t('Type'), value: r => this.$t(this.typeLabel(r)), width: 18 },
        { header: this.$t('Document'), value: r => this.documentLabel(r), width: 28 },
        // ช่องทางขาย — ว่าง = ขายหน้าร้าน · มีค่า = ออเดอร์แพลตฟอร์ม (เงินไปเก็บสิ้นเดือน)
        { header: this.$t('Channel'), value: r => r.channel || this.$t('In-store'), width: 16 },
        { header: this.$t('Invoice No.') + ' / ' + this.$t('Bill No.'), value: r => (r.type === 'abbr_daily' ? this.billRangeLabel(r) : r.bill_no), width: 34 },
        { header: this.$t('Customer'), key: 'customer_name', width: 28 },
        { header: this.$t('Customer Tax ID'), key: 'customer_tax_id', width: 18 },
        { header: this.$t('Customer Branch No'), key: 'customer_branch', width: 12 },
        { header: this.$t('Taxable'), value: r => this.toNum(r.taxable_amount), width: 14, numFmt: MONEY_FMT },
        { header: this.$t('VAT'), value: r => this.toNum(r.vat_amount), width: 14, numFmt: MONEY_FMT },
        { header: this.$t('Total Amount'), value: r => this.toNum(r.total_amount), width: 16, numFmt: MONEY_FMT },
        // 2 คอลัมน์นี้คือสิ่งที่บัญชีต้องใช้ออกใบลดหนี้เอง — เลขใบกำกับเดิมที่ถูกลด + เหตุผล
        { header: this.$t('Original Tax Invoice No.'), value: r => (r.type === 'refund' ? r.invoice_no : ''), width: 24 },
        { header: this.$t('Reason'), key: 'void_reason', width: 28 },
        { header: this.$t('Status'), value: r => (r.is_voided ? this.$t('Voided') : this.$t('Active')), width: 12 },
      ];
      const totals = this.visibleTotals;
      exportRowsToXlsx({
        filename: `vat-sales-${from}-${to}.xlsx`,
        sheetName: 'VAT Sales',
        columns,
        rows: this.visibleRows,
        preRows: header,
        postRows: [[
          this.$t('Total'), '', '', '', '', '', '', '',
          totals.taxable, totals.vat, totals.total, '', '', '',
        ]],
      });
    },

    /**
     * ชุดภาษีประจำงวดสำหรับส่งบัญชี — ไฟล์ Excel เดียว 3 ชีต
     * (สรุป / ภาษีขาย / ภาษีซื้อ) เป็นคำตอบของ "ส่งไฟล์ให้บัญชีคุณได้เลย"
     * ดู silver-spoon-api/docs/vat-compliance.md §1 โซน 2
     */
    async exportTaxPackage() {
      this.packing = true;
      const from = this.formatDate(this.filters.date_range.startDate);
      const to = this.formatDate(this.filters.date_range.endDate);

      try {
        // ภาษีซื้อเป็นคนละสิทธิ์กับภาษีขาย — คนที่ไม่มีสิทธิ์ต้องยัง export ส่วนที่ตัวเองดูได้
        let purchase = null;
        try {
          const res = await Reports.vatPurchaseList(this.buildFetchParams());
          purchase = res?.data?.data || null;
        } catch (e) {
          this.$toast.warn(this.$t('Input VAT could not be included — you do not have access to that report.'));
        }

        const outputVat = this.toNum(this.summary.total_vat);
        const claimableInputVat = this.toNum(purchase?.summary?.total_vat_claimable);

        const sheets = [
          {
            sheetName: this.$t('Summary'),
            columns: [
              { header: this.$t('Item'), key: 'label', width: 34 },
              { header: this.$t('Amount'), key: 'value', width: 18, numFmt: MONEY_FMT },
            ],
            preRows: [
              [this.$t('VAT Package')],
              [this.selectedBranchLabel + '  |  ' + from + ' — ' + to],
              [],
            ],
            rows: [
              { label: this.$t('Gross Sales'), value: this.toNum(this.summary.gross_sales) },
              { label: this.$t('Total Taxable'), value: this.toNum(this.summary.total_taxable) },
              { label: this.$t('Service Charge'), value: this.toNum(this.summary.service_charge) },
              { label: this.$t('Output VAT'), value: outputVat },
              { label: this.$t('Claimable Input VAT'), value: claimableInputVat },
              { label: this.$t('Output VAT less input VAT'), value: outputVat - claimableInputVat },
            ],
            postRows: [
              [],
              // กันเข้าใจผิดว่าเป็นยอด ภ.พ.30 พร้อมยื่น — เราไม่รู้เครดิตยกมาจากเดือนก่อน
              [this.$t('This is a starting figure for your bookkeeper — it does not include VAT credit carried forward from previous months.')],
            ],
          },
          {
            sheetName: this.$t('VAT Sales List'),
            columns: [
              { header: '#', key: 'seq', width: 6 },
              { header: this.$t('Date'), key: 'date', width: 12 },
              { header: this.$t('Type'), value: r => this.$t(this.typeLabel(r)), width: 18 },
              { header: this.$t('Document'), value: r => this.documentLabel(r), width: 28 },
              { header: this.$t('Channel'), value: r => r.channel || this.$t('In-store'), width: 16 },
              { header: this.$t('Invoice No.') + ' / ' + this.$t('Bill No.'), value: r => (r.type === 'abbr_daily' ? this.billRangeLabel(r) : r.bill_no), width: 34 },
              { header: this.$t('Customer'), key: 'customer_name', width: 28 },
              { header: this.$t('Customer Tax ID'), key: 'customer_tax_id', width: 18 },
              { header: this.$t('Taxable'), value: r => this.toNum(r.taxable_amount), width: 14, numFmt: MONEY_FMT },
              { header: this.$t('VAT'), value: r => this.toNum(r.vat_amount), width: 14, numFmt: MONEY_FMT },
              { header: this.$t('Total Amount'), value: r => this.toNum(r.total_amount), width: 16, numFmt: MONEY_FMT },
              // บรรทัดหักยอดคืนเงิน: บัญชีต้องรู้ว่าไปลดใบกำกับเลขไหน เพราะอะไร ถึงจะออกใบลดหนี้ได้
              { header: this.$t('Original Tax Invoice No.'), value: r => (r.type === 'refund' ? r.invoice_no : ''), width: 24 },
              { header: this.$t('Reason'), key: 'void_reason', width: 28 },
            ],
            rows: this.invoices,
            postRows: [[
              this.$t('Total'), '', '', '', '', '', '', '',
              this.toNum(this.summary.total_taxable),
              outputVat,
              this.toNum(this.summary.total_amount),
              '', '',
            ]],
          },
        ];

        if (purchase) {
          sheets.push({
            sheetName: this.$t('VAT Purchase List'),
            columns: [
              { header: '#', key: 'seq', width: 6 },
              { header: this.$t('Date'), key: 'date', width: 12 },
              { header: this.$t('Source'), value: r => this.$t(r.source === 'expense' ? 'Expense' : 'Goods Receipt'), width: 16 },
              { header: this.$t('Tax Invoice No.'), key: 'tax_invoice_no', width: 22 },
              { header: this.$t('Supplier'), key: 'supplier_name', width: 28 },
              { header: this.$t('Supplier') + ' ' + this.$t('Tax ID'), key: 'supplier_tax_id', width: 18 },
              { header: this.$t('Branch No'), key: 'supplier_branch', width: 12 },
              { header: this.$t('Taxable'), value: r => this.toNum(r.taxable_amount), width: 14, numFmt: MONEY_FMT },
              { header: this.$t('VAT'), value: r => this.toNum(r.vat_amount), width: 14, numFmt: MONEY_FMT },
              { header: this.$t('Total Amount'), value: r => this.toNum(r.total_amount), width: 16, numFmt: MONEY_FMT },
              { header: this.$t('Claimable'), value: r => (r.is_vat_claimable ? this.$t('Yes') : this.$t('No')), width: 12 },
            ],
            rows: purchase.invoices || [],
            postRows: [[
              this.$t('Total'), '', '', '', '', '', '',
              this.toNum(purchase.summary?.total_taxable),
              this.toNum(purchase.summary?.total_vat),
              this.toNum(purchase.summary?.total_amount),
              '',
            ]],
          });
        }

        exportSheetsToXlsx({ filename: `tax-package-${from}-${to}.xlsx`, sheets });
      } finally {
        this.packing = false;
      }
    },

    async buildPdfDefinition() {
      const kpis = {
        columns: [
          { text: [{ text: this.$t('Total Taxable') + '\n', style: 'kpiLabel' }, { text: this.formatCurrencyRaw(this.summary.total_taxable), style: 'kpiValue' }], width: '*' },
          { text: [{ text: this.$t('Total VAT') + '\n', style: 'kpiLabel' }, { text: this.formatCurrencyRaw(this.summary.total_vat), style: 'kpiValue' }], width: '*' },
          { text: [{ text: this.$t('Service Charge') + '\n', style: 'kpiLabel' }, { text: this.formatCurrencyRaw(this.summary.service_charge), style: 'kpiValue' }], width: '*' },
          { text: [{ text: this.$t('Total Amount') + '\n', style: 'kpiLabel' }, { text: this.formatCurrencyRaw(this.summary.total_amount), style: 'kpiValue' }], width: '*' },
        ],
        margin: [0, 0, 0, 16],
      };

      if (this.activeTab === 'daily') {
        return {
          content: [
            ...this.buildPdfHeader(),
            { text: this.$t('VAT Sales List') + ' — ' + this.$t('Daily Summary'), style: 'sectionTitle' },
            kpis,
            {
              table: {
                headerRows: 1,
                widths: ['auto', '*', '*', '*', '*'],
                body: [
                  [
                    { text: this.$t('Date'), style: 'tableHeader' },
                    { text: this.$t('Gross Sales'), style: 'tableHeader', alignment: 'right' },
                    { text: this.$t('Net Sales'), style: 'tableHeader', alignment: 'right' },
                    { text: this.$t('VAT'), style: 'tableHeader', alignment: 'right' },
                    { text: this.$t('Service Charge'), style: 'tableHeader', alignment: 'right' },
                  ],
                  ...this.daily.map(r => ([
                    { text: r.date, style: 'tableCell' },
                    { text: this.formatCurrencyRaw(r.gross), style: 'tableCell', alignment: 'right' },
                    { text: this.formatCurrencyRaw(r.net), style: 'tableCell', alignment: 'right' },
                    { text: this.formatCurrencyRaw(r.vat), style: 'tableCell', alignment: 'right' },
                    { text: this.formatCurrencyRaw(r.service_charge), style: 'tableCell', alignment: 'right' },
                  ])),
                  [
                    { text: this.$t('Total'), style: 'tableHeader' },
                    { text: this.formatCurrencyRaw(this.summary.gross_sales), style: 'tableHeader', alignment: 'right' },
                    { text: this.formatCurrencyRaw(this.summary.total_taxable), style: 'tableHeader', alignment: 'right' },
                    { text: this.formatCurrencyRaw(this.summary.total_vat), style: 'tableHeader', alignment: 'right' },
                    { text: this.formatCurrencyRaw(this.summary.service_charge), style: 'tableHeader', alignment: 'right' },
                  ],
                ],
              },
              layout: 'lightHorizontalLines',
            },
          ],
        };
      }

      /**
       * รายงานภาษีขายรายเครื่อง = "รายงานการขายประจำวัน" ที่ ภ.พ.06 ขอให้แนบเป็นตัวอย่าง
       * และเป็นตัวที่สรรพากรใช้ตรวจว่าเลขใบกำกับของแต่ละทะเบียนเครื่องรันต่อเนื่องและยอดตรง
       * จึงต้องพิมพ์ได้จริง — เดิมแท็บนี้ตกไปใช้ตารางเอกสารรายใบ ได้ PDF ผิดหัวเรื่องผิดคอลัมน์
       */
      if (this.activeTab === 'devices') {
        return {
          /**
           * 11 คอลัมน์ลง A4 แนวตั้งไม่ได้ (วัดแล้วกินพื้นที่ 859pt จากที่มี 555pt)
           * ต้องแนวนอน **และ** กำหนดความกว้างเป็นตัวเลขตายตัวทุกคอลัมน์
           *
           * ห้ามใช้ 'auto' ที่นี่ — 'auto' ขยายตามเนื้อหา พอเจอยอดสะสมหลักร้อยล้าน
           * หรือชื่อสาขาไทยยาว ๆ (ภาษาไทยไม่มีช่องว่าง ตัดคำไม่ได้) ตารางจะดันทะลุขอบอีก
           * ความกว้างตายตัวทำให้เนื้อหายาวเกิน "ตัดบรรทัด" แทนที่จะ "ดันหน้ากระดาษ"
           *
           * รวม 590pt + ค่าคงที่ของ pdfmake ≈ 790pt จากพื้นที่ 802pt (วัดจริงแล้ว)
           */
          pageOrientation: 'landscape',
          content: [
            ...this.buildPdfHeader(),
            { text: this.$t('VAT Sales List') + ' — ' + this.$t('By POS Device'), style: 'sectionTitle' },
            kpis,
            {
              table: {
                headerRows: 1,
                widths: [42, 66, 38, 24, 180, 50, 44, 50, 54, 54],
                body: [
                  [
                    { text: this.$t('Date'), style: 'tableHeader' },
                    { text: this.$t('POS Device'), style: 'tableHeader' },
                    { text: this.$t('POS ID'), style: 'tableHeader' },
                    { text: this.$t('Documents'), style: 'tableHeader', alignment: 'right' },
                    { text: this.$t('Invoice Numbers Issued'), style: 'tableHeader' },
                    { text: this.$t('Taxable'), style: 'tableHeader', alignment: 'right' },
                    { text: this.$t('VAT'), style: 'tableHeader', alignment: 'right' },
                    { text: this.$t('Total Amount'), style: 'tableHeader', alignment: 'right' },
                    { text: this.$t('Previous Grand Total'), style: 'tableHeader', alignment: 'right' },
                    { text: this.$t('Current Grand Total'), style: 'tableHeader', alignment: 'right' },
                  ],
                  ...this.devices.map(r => ([
                    { text: r.date, style: 'tableCell' },
                    {
                      text: (r.device_name || this.$t('Unassigned device')) + (r.branch ? `\n${r.branch}` : ''),
                      style: 'tableCell',
                    },
                    { text: r.pos_device_code || '-', style: 'tableCell' },
                    { text: String(r.bill_count ?? 0), style: 'tableCell', alignment: 'right' },
                    {
                      text: r.invoice_no_list ? `${r.invoice_no_prefix}\n${r.invoice_no_list}` : '-',
                      style: 'tableCell',
                    },
                    { text: this.formatCurrencyRaw(r.taxable_amount), style: 'tableCell', alignment: 'right' },
                    { text: this.formatCurrencyRaw(r.vat_amount), style: 'tableCell', alignment: 'right' },
                    { text: this.formatCurrencyRaw(r.total_amount), style: 'tableCell', alignment: 'right' },
                    {
                      text: r.grand_total_before === null ? '-' : this.formatCurrencyRaw(r.grand_total_before),
                      style: 'tableCell',
                      alignment: 'right',
                    },
                    {
                      text: r.grand_total_after === null ? '-' : this.formatCurrencyRaw(r.grand_total_after),
                      style: 'tableCell',
                      alignment: 'right',
                    },
                  ])),
                  [
                    { text: this.$t('Total'), style: 'tableHeader', colSpan: 5 }, {}, {}, {}, {},
                    { text: this.formatCurrencyRaw(this.summary.total_taxable), style: 'tableHeader', alignment: 'right' },
                    { text: this.formatCurrencyRaw(this.summary.total_vat), style: 'tableHeader', alignment: 'right' },
                    { text: this.formatCurrencyRaw(this.summary.total_amount), style: 'tableHeader', alignment: 'right' },
                    {}, {},
                  ],
                ],
              },
              layout: 'lightHorizontalLines',
            },
            // เตือนคนอ่านรายงาน ห้ามตีความ "เลขแรก–เลขสุดท้าย" เป็นช่วงต่อเนื่อง
            // counter เดินต่อสาขา หลายเครื่องในสาขาเดียวกันจึงสลับเลขกัน (vat-compliance.md D10)
            {
              text: '* ' + this.$t('First/Last No. are not a continuous range — the counter runs per branch so devices in the same branch interleave numbers.'),
              style: 'tableCell',
              margin: [0, 10, 0, 0],
            },
            // ยอดขายสะสม — เจ้าหน้าที่ตรวจว่าเลขนี้เดินหน้าอย่างเดียว ไม่เคยถูกรีเซ็ต
            {
              text: '* ' + this.$t('Grand Total runs continuously per device from its first bill and is never reset.'),
              style: 'tableCell',
              margin: [0, 2, 0, 0],
            },
          ],
        };
      }

      const totals = this.visibleTotals;
      // 8 คอลัมน์ + ชื่อลูกค้าไทยยาว ๆ ล้น A4 แนวตั้ง (วัดได้ 608pt จากที่มี 555pt)
      // แนวนอนพอ เพราะคอลัมน์ลูกค้าเป็น '*' ซึ่งยืดหดรับส่วนที่เหลือให้เอง
      const content = [
        ...this.buildPdfHeader(),
        {
          text: this.$t('VAT Sales List') + ' — '
            + this.$t(this.activeTab === 'full' ? 'Full Tax Invoices' : 'All Documents'),
          style: 'sectionTitle',
        },
        kpis,
        {
          table: {
            headerRows: 1,
            widths: [18, 'auto', 'auto', 'auto', '*', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: '#', style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Date'), style: 'tableHeader' },
                { text: this.$t('Document'), style: 'tableHeader' },
                { text: this.$t('Bill No.'), style: 'tableHeader' },
                { text: this.$t('Customer'), style: 'tableHeader' },
                { text: this.$t('Taxable'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('VAT'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Total Amount'), style: 'tableHeader', alignment: 'right' },
              ],
              ...this.visibleRows.map(r => {
                const cell = (text, extra = {}) => ({
                  text,
                  style: 'tableCell',
                  color: r.is_voided ? '#b91c1c' : undefined,
                  decoration: r.is_voided ? 'lineThrough' : undefined,
                  ...extra,
                });
                const name = r.type === 'abbr_daily'
                  ? this.$t('Walk-in customers')
                  : (r.customer_name || '') + (r.is_voided ? '  (' + this.$t('Voided') + ')' : '');
                return [
                  cell(String(r.seq), { alignment: 'right' }),
                  cell(r.date),
                  cell(this.documentLabel(r)),
                  cell(r.type === 'abbr_daily' ? this.billRangeLabel(r) : r.bill_no),
                  cell(name),
                  cell(this.formatCurrencyRaw(r.taxable_amount), { alignment: 'right' }),
                  cell(this.formatCurrencyRaw(r.vat_amount), { alignment: 'right' }),
                  cell(this.formatCurrencyRaw(r.total_amount), { alignment: 'right' }),
                ];
              }),
              [
                { text: this.$t('Total'), style: 'tableHeader', colSpan: 5 }, {}, {}, {}, {},
                { text: this.formatCurrencyRaw(totals.taxable), style: 'tableHeader', alignment: 'right' },
                { text: this.formatCurrencyRaw(totals.vat), style: 'tableHeader', alignment: 'right' },
                { text: this.formatCurrencyRaw(totals.total), style: 'tableHeader', alignment: 'right' },
              ],
            ],
          },
          layout: 'lightHorizontalLines',
        },
      ];
      return { pageOrientation: 'landscape', content };
    },
  },
};
</script>

<style scoped>
.report-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.report-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}
.table-danger-subtle {
  background-color: #fdf0f0;
}
/* บรรทัดสรุปรายวันของใบกำกับอย่างย่อ — ไม่ใช่เอกสารรายใบ ให้ต่างจากบรรทัดใบกำกับจริง */
.table-abbr-daily {
  background-color: #f8fafc;
}
</style>
